import { loadingScreen } from '../ts/loading';
import { generateConfirmationBox } from '../ts/confirmationBox';
import { notificationHandler } from '../ts/common';
import { setCookie } from './cookieManager.js';
import 'https://js.stripe.com/v3';

const CURRENCY_PREFERENCE_COOKIE = 'CurrencyPreference';
const CURRENCY_PREFERENCE_MAX_AGE_DAYS = 365;

var stripe;

function persistCurrencyPreference(currencyCode) {
    const code = (currencyCode || '').toLowerCase();
    if (code === 'gbp' || code === 'usd') {
        setCookie(CURRENCY_PREFERENCE_COOKIE, code, CURRENCY_PREFERENCE_MAX_AGE_DAYS);
    }
}

function init(
    stripeKey, selectedPlan, trialPeriodDays, trialPeriodMonths, couponID, clientName, showCurencyToggle,
    checkoutSessionURL, removeCancelURL, modifySubscriptionURL, redeemURL
    ) {
	stripe = Stripe(stripeKey);
	if (selectedPlan) {
        subscribe(checkoutSessionURL, selectedPlan, trialPeriodDays, trialPeriodMonths, true, couponID)();
    }

	displayClientReferralBonus(clientName, trialPeriodDays, trialPeriodMonths);
	initialisePricingFilters(showCurencyToggle);
	initialiseActionButtons(removeCancelURL, modifySubscriptionURL, checkoutSessionURL, redeemURL, trialPeriodDays, trialPeriodMonths);
}

function displayClientReferralBonus(clientName, trialPeriodDays, trialPeriodMonths) {
    if (clientName && (trialPeriodDays + trialPeriodMonths) > 0) {
        let message = '';
        if (trialPeriodDays > 0 && trialPeriodMonths > 0) {
            message = 'You are eligible for a ' + trialPeriodMonths + ' month and ' + trialPeriodDays + ' day free trial courtesy of ' + clientName;
        } else if (trialPeriodDays > 0) {
            message = 'You are eligible for a ' + trialPeriodDays + ' day free trial courtesy of ' + clientName;
        } else if (trialPeriodMonths > 0) {
            message = 'You are eligible for a ' + trialPeriodMonths + ' month free trial courtesy of ' + clientName;
        }
        notificationHandler(message, "success");
    }
}

function initialisePricingFilters(showCurrencyToggle) {
    const pricingTable = document.getElementById('pricing-table');

    const paymentPeriods = ['monthly', 'annual']
    paymentPeriods.forEach(function(paymentPeriod) {
        let paymentPeriodToggle = document.getElementById(paymentPeriod);
        paymentPeriodToggle.addEventListener('change', function(e) {
            if (e.target.checked) {
                pricingTable.classList.remove(...paymentPeriods);
                pricingTable.classList.add(paymentPeriod);
            }
        });
    });

    if (showCurrencyToggle) {
        const currencies = ['gbp', 'usd']
        currencies.forEach(function(currency) {
            let currencyPeriodToggle = document.getElementById(currency);
            currencyPeriodToggle.addEventListener('change', function(e) {
                if (e.target.checked) {
                    pricingTable.classList.remove(...currencies);
                    pricingTable.classList.add(currency);
                    persistCurrencyPreference(currency);
                }
            });
        });
    }
}

function initialiseActionButtons(removeCancelUrl, modifySubscriptionUrl, createCheckoutSessionUrl, redeemUrl, trialPeriodDays, trialPeriodMonths) {
    const actionButtons = document.querySelectorAll('button.action');

    actionButtons.forEach(function(actionButton) {
        if (actionButton.classList.contains('remove-cancel')) {
            actionButton.addEventListener('click', generateConfirmationBox(actionButton.dataset.confirmationtitle, actionButton.dataset.confirmationdescription, removeCancel(removeCancelUrl)));
        } else if (actionButton.classList.contains('modify-subscription')) {
            actionButton.addEventListener('click', generateConfirmationBox(actionButton.dataset.confirmationtitle, actionButton.dataset.confirmationdescription, modifySubscription(modifySubscriptionUrl, actionButton.dataset.modification, actionButton.dataset.planid, actionButton.dataset.couponid)));
        } else if (actionButton.classList.contains('subscribe')) {
            actionButton.addEventListener('click', subscribe(createCheckoutSessionUrl, actionButton.dataset.planid, trialPeriodDays, trialPeriodMonths, false, actionButton.dataset.couponid));
        } else if (actionButton.classList.contains('redeem')) {
            actionButton.addEventListener('click', generateConfirmationBox(actionButton.dataset.confirmationtitle, actionButton.dataset.confirmationdescription, redeem(redeemUrl, actionButton.dataset.planid)));
        }
    });
}

function removeCancel(url) {
    return async evt => {
        loadingScreen(true, false);
        try {
            const response = await fetch(url, {method: 'POST'});
            if (response.ok) {
                const data = await response.json();  // Reload page to update the pricing table
                document.location.reload(true);
            } else {
                throw new Error('Error: ' + response.status + ' ' + response.statusText);
            }
        } catch (error) {
            console.error(url + ' Fetch API Error: ', error);
        }
    }
}

function modifySubscription(url, modification, planId, couponID) {
    return async evt => {
        loadingScreen(true, false);
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    modification: modification,
                    planId: planId,
                    couponId: couponID
                })
            })
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Unable to change your subscription. Please try again.');
            }
            if (data.result === 'REQUIRES_ACTION' && data.clientSecret) {
                // Release the modal loading screen so Stripe's authentication UI can receive focus.
                loadingScreen(false);
                const result = await stripe.confirmCardPayment(data.clientSecret);
                if (result.error) {
                    throw new Error(result.error.message || 'Authentication failed. Your plan has not changed.');
                }
                if (!result.paymentIntent || result.paymentIntent.status !== 'succeeded') {
                    throw new Error('Your upgrade payment has not completed. Your plan has not changed.');
                }
            } else if (data.result !== 'SUCCESS') {
                throw new Error('Your upgrade payment has not completed. Please try again.');
            }
            document.location.reload(true);
        } catch (error) {
            console.error(url + ' Subscription modification error: ', error);
            logFrontendError(url + ' Subscription modification error: ', error);
            loadingScreen(false);
            notificationHandler(error.message || 'Unable to change your subscription. Please try again.', 'error');
        }
    }
}

function subscribe(url, planId, trialPeriodDays, trialPeriodMonths, skippedRegisterDetails, couponID) {
    return async evt => {
        loadingScreen(true, false);
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    planId: planId,
                    skippedRegisterDetails: skippedRegisterDetails,
                    trialPeriodDays: trialPeriodDays,
                    trialPeriodMonths: trialPeriodMonths,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    couponId: couponID
                })
            })
            if (response.ok) {
                const data = await response.json()
                // Redirect to Checkout
                stripe.redirectToCheckout({
                    sessionId: data.session
                })
                .then(function (result) {
                    if (result.error) {
                        // If `redirectToCheckout` fails due to a browser or network
                        // error, display the localized error message to your customer.
                        // TODO
                        // var displayError = document.getElementById('error-message');
                        // displayError.textContent = result.error.message;
                        // displayError.style.visibility="visible";
                    }
                });
            } else {
                throw new Error('Error: ' + response.status + ' ' + response.statusText)
            }
        } catch (error) {
            console.error(url + ' Fetch API Error: ', error)
        }
    }
}

function redeem(url, planId) {
    return async evt => {
        loadingScreen(true, false);
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    planId: planId
                })
            })
            if (response.ok) {
                const data = await response.json()
                document.location.reload(true)
            } else {
                throw new Error('Error: ' + response.status + ' ' + response.statusText)
            }
        } catch (error) {
            console.error(url + ' Fetch API Error: ', error)
        }
    }
}


// ------------------ INITIALISATION ------------------

const data = window.pricingData;

init(
    data.stripeKey,
    data.selectedPlan,
    data.trialPeriodDays,
    data.trialPeriodMonths,
    data.couponID,
    data.clientName,
    data.showCurencyToggle,
    data.checkoutSessionURL,
    data.removeCancelURL,
    data.modifySubscriptionURL,
    data.redeemURL
);